<svelte:options
  customElement={{
    tag: "line-chart",
    shadow: "none",
    props: {
      title: {
        type: "String",
      },
      series: {
        type: "Array",
      },
      showDelta: { type: "Boolean" },
      formatter: {
        type: "String",
      },
    },
  }}
/>

<script>
  import ApexCharts from "apexcharts";
  let { title, series, showDelta = false, formatter = "Number" } = $props();

  let numberFormatter = $derived.by(() => {
    if (formatter === "Number") {
      return new Intl.NumberFormat("en-US", {
        style: "decimal",
        maximumFractionDigits: 2,
      });
    } else if (formatter === "Currency") {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      });
    } else if (formatter === "Percent") {
      return new Intl.NumberFormat("en-US", {
        style: "percent",
        maximumFractionDigits: 2,
      });
    } else {
      return { format: (value) => value };
    }
  });

  let count = $derived(
    numberFormatter.format(series[1].data[series[1].data.length - 1]),
  );

  let deltaPositive = $state(false);
  let delta = $state(0.0);

  $effect(() => {
    let a = series[0].data[series[0].data.length - 1];
    let b = series[1].data[series[1].data.length - 1];
    let value = b - a;
    deltaPositive = value > 0;
    delta = `${deltaPositive ? "+" : ""}${numberFormatter.format(value)}`;
  });

  let ready = $state(false);

  function init(element) {
    // Prevent the chart from being updated by Phoenix LiveView once we're connected.
    $host().setAttribute("phx-update", "ignore");

    const chart = new ApexCharts(element, {
      chart: {
        type: "line",
        toolbar: { show: false },
        height: 75,
        animations: {
          enabled: true,
          speed: 500,
          dynamicAnimation: {
            enabled: true,
          },
        },
        sparkline: {
          enabled: true,
        },
        events: {
          mounted: () => {
            ready = true;
          },
        },
      },
      grid: {
        padding: {
          right: 15,
        },
      },
      colors: ["#d4d4d8"],
      markers: {
        discrete: [
          {
            seriesIndex: 0,
            dataPointIndex: series[0].data.length - 1,
            size: 2,
            fillColor: "#d4d4d8",
            strokeColor: "#d4d4d8",
          },
          {
            seriesIndex: 1,
            dataPointIndex: series[1].data.length - 1,
            size: 2,
            fillColor: "#93c5fd",
            strokeColor: "#93c5fd",
          },
        ],
      },
      dataLabels: {
        enabled: true,
        enabledOnSeries: [1],
        style: {
          colors: ["#000", "#fff"],
          fontFamily: "Inter, Helvetica, Arial, sans-serif",
        },
        background: {
          enabled: true,
          opacity: 1,
          foreColor: "#000",
        },
        formatter: (val, opts) => {
          return numberFormatter.format(val);
        },
      },
      stroke: {
        curve: "smooth",
        width: 3,
        dashArray: [4, 0],
      },
      fill: {
        type: ["solid", "gradient"],
        gradient: {
          colorStops: [
            {
              offset: 0,
              color: "#60a5fa",
            },
            {
              offset: 0.5,
              color: "#3b82f6",
            },
            {
              offset: 100,
              color: "#2563eb",
            },
          ],
        },
      },
      series: series,
      tooltip: {
        enabled: false,
      },
    });
    chart.render();
  }
</script>

<div
  class="tw:min-h-full tw:min-w-[250px] tw:overflow-hidden tw:rounded-lg tw:bg-white tw:px-4 tw:py-5 tw:opacity-100 tw:shadow tw:ring-1 tw:ring-zinc-900/5 tw:transition-all tw:delay-700 tw:duration-800"
>
  {#if title}
    <h2 class="tw:truncate tw:text-sm tw:font-medium tw:text-zinc-500">
      {title}
    </h2>
  {/if}
  <div class="tw:flex tw:flex-row tw:items-start tw:gap-1">
    <h1
      class="tw:text-3xl tw:font-semibold tw:tracking-tight tw:text-zinc-900 tw:tabular-nums"
    >
      {count}
    </h1>
    <div
      class={[
        "tw:flex tw:items-center tw:gap-1 tw:pt-1",
        deltaPositive && "tw:text-lime-600",
        !deltaPositive && "tw:text-red-600",
      ]}
    >
      {#if showDelta}
        {#if !deltaPositive}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            class="tw:size-4"
          >
            <path
              fill-rule="evenodd"
              d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14Zm.75-10.25a.75.75 0 0 0-1.5 0v4.69L6.03 8.22a.75.75 0 0 0-1.06 1.06l2.5 2.5a.75.75 0 0 0 1.06 0l2.5-2.5a.75.75 0 1 0-1.06-1.06L8.75 9.44V4.75Z"
              clip-rule="evenodd"
            />
          </svg>
        {:else}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            class="tw:size-4"
          >
            <path
              fill-rule="evenodd"
              d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1Zm-.75 10.25a.75.75 0 0 0 1.5 0V6.56l1.22 1.22a.75.75 0 1 0 1.06-1.06l-2.5-2.5a.75.75 0 0 0-1.06 0l-2.5 2.5a.75.75 0 0 0 1.06 1.06l1.22-1.22v4.69Z"
              clip-rule="evenodd"
            />
          </svg>
        {/if}

        <h3
          class={[
            "tw:text-sm",
            deltaPositive && "tw:text-lime-600",
            !deltaPositive && "tw:text-red-600",
          ]}
        >
          {delta}
        </h3>
      {/if}
    </div>
  </div>
  <figure
    class="tw:relative tw:isolate tw:-mx-4 tw:mt-2 tw:min-h-[75px] tw:before:absolute tw:before:inset-0 tw:before:z-10 tw:before:w-1/3 tw:before:bg-linear-to-r tw:before:from-white tw:before:to-transparent"
  >
    <div
      class={[
        "tw:transition-opacity",
        ready ? "tw:opacity-100" : "tw:opacity-0",
      ]}
      {@attach init}
    ></div>
  </figure>
</div>
